import React, { useEffect, useRef, useCallback } from 'react';

interface SpaceInvadersProps {
  onActivity: () => void;
}

interface GameState {
  player: { x: number; y: number; width: number; height: number };
  bullets: Array<{ x: number; y: number; width: number; height: number }>;
  enemies: Array<{ x: number; y: number; width: number; height: number; alive: boolean }>;
  score: number;
  gameSpeed: number;
}

export const SpaceInvaders: React.FC<SpaceInvadersProps> = ({ onActivity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameStateRef = useRef<GameState | null>(null);
  const animationRef = useRef<number>(0);
  const keysRef = useRef<Set<string>>(new Set());

  const initGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    // Initialize enemies in a grid
    const enemies = [];
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 10; col++) {
        enemies.push({
          x: col * 60 + 100,
          y: row * 40 + 50,
          width: 40,
          height: 30,
          alive: true
        });
      }
    }

    return {
      player: { 
        x: canvas.width / 2 - 25, 
        y: canvas.height - 60, 
        width: 50, 
        height: 30 
      },
      bullets: [],
      enemies,
      score: 0,
      gameSpeed: 1
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gameStateRef.current = initGame();
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize game
    gameStateRef.current = initGame();

    // Keyboard handlers
    const handleKeyDown = (e: KeyboardEvent) => {
      keysRef.current.add(e.code);
      if (e.code === 'Space') {
        e.preventDefault();
        // Fire bullet
        const game = gameStateRef.current;
        if (game) {
          game.bullets.push({
            x: game.player.x + game.player.width / 2 - 2,
            y: game.player.y,
            width: 4,
            height: 10
          });
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.code);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const gameLoop = () => {
      const game = gameStateRef.current;
      if (!game) return;

      // Clear canvas
      ctx.fillStyle = 'rgba(0, 0, 20, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update player movement
      if (keysRef.current.has('ArrowLeft') && game.player.x > 0) {
        game.player.x -= 5;
      }
      if (keysRef.current.has('ArrowRight') && game.player.x < canvas.width - game.player.width) {
        game.player.x += 5;
      }

      // Update bullets
      game.bullets = game.bullets.filter(bullet => {
        bullet.y -= 8;
        return bullet.y > 0;
      });

      // Check bullet-enemy collisions
      game.bullets = game.bullets.filter(bullet => {
        for (let enemy of game.enemies) {
          if (enemy.alive && 
              bullet.x < enemy.x + enemy.width &&
              bullet.x + bullet.width > enemy.x &&
              bullet.y < enemy.y + enemy.height &&
              bullet.y + bullet.height > enemy.y) {
            enemy.alive = false;
            game.score += 10;
            return false; // Remove bullet
          }
        }
        return true;
      });

      // Move enemies
      const aliveEnemies = game.enemies.filter(e => e.alive);
      if (aliveEnemies.length === 0) {
        // Respawn enemies
        game.enemies = initGame()?.enemies || [];
        game.gameSpeed *= 1.2;
      } else {
        // Simple enemy movement
        aliveEnemies.forEach(enemy => {
          enemy.x += Math.sin(Date.now() * 0.001 + enemy.y * 0.01) * 0.5;
          enemy.y += 0.2 * game.gameSpeed;
        });
      }

      // Draw player (spaceship)
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(game.player.x, game.player.y, game.player.width, game.player.height);
      
      // Draw player details
      ctx.fillStyle = '#ffff00';
      ctx.fillRect(game.player.x + 20, game.player.y - 5, 10, 5); // cockpit

      // Draw bullets
      ctx.fillStyle = '#ffffff';
      game.bullets.forEach(bullet => {
        ctx.fillRect(bullet.x, bullet.y, bullet.width, bullet.height);
      });

      // Draw enemies
      game.enemies.forEach(enemy => {
        if (enemy.alive) {
          ctx.fillStyle = '#ff0040';
          ctx.fillRect(enemy.x, enemy.y, enemy.width, enemy.height);
          
          // Enemy details
          ctx.fillStyle = '#ff8080';
          ctx.fillRect(enemy.x + 5, enemy.y + 5, 30, 20);
          ctx.fillStyle = '#ff0000';
          ctx.fillRect(enemy.x + 15, enemy.y + 10, 10, 5); // eyes
        }
      });

      // Draw score
      ctx.fillStyle = '#ffffff';
      ctx.font = '24px monospace';
      ctx.fillText(`SCORE: ${game.score}`, 20, 40);

      // Draw stars background
      for (let i = 0; i < 100; i++) {
        const x = (i * 37) % canvas.width;
        const y = ((Date.now() * 0.05 + i * 50) % canvas.height);
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.5})`;
        ctx.fillRect(x, y, 1, 1);
      }

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [initGame]);

  return (
    <div 
      className="fixed inset-0 bg-black cursor-none z-[9999]"
      onClick={onActivity}
      onMouseMove={onActivity}
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-full"
      />
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white text-lg opacity-60 font-mono text-center">
        <div>Use ← → arrows to move, SPACE to shoot</div>
        <div className="text-sm mt-1">Move mouse or press ESC to exit</div>
      </div>
    </div>
  );
};